import React, { Component } from 'react';
import { connect } from 'react-redux';
import './DepositDetails.css';
import numeral from 'numeral';
import {
  getCurrentDeposit,
  getPdf,
  getShowPdfViewer,
  getUsersByClientId,
  getShowEmailModal,
} from 'selectors/admin/deposits/deposits';
import RouteTitle from 'components/shared/route-title/RouteTitle';
import {
  loadDepositRequest,
  markAsPaidRequest,
  printDeposit,
  closePdfViewer,
  loadUsersRequest,
  setShowEmailModal,
  sendEmail,
} from 'actions/admin/deposits/deposits';
import Date from 'components/shared/text/Date';
import Number from 'components/shared/text/Number';
import SimpleTable from 'components/shared/table/SimpleTable';
import { TaskStatusKeys } from 'domain/task-status-enum';
import checkSquarePng from 'images/check-square.png';
import Button from 'components/shared/button/Button';
import mailWhiteSvg from 'images/mail-white.svg';
import printWhiteSvg from 'images/print-white.svg';
import PdfViewer from 'components/shared/pdf-viewer/PdfViewer';
import { EmailModal } from 'components/shared/email/EmailModal';
import { WorkOrder } from 'domain/work-order';
import { getCurrentUser } from 'selectors/authentication';
import { ROLES, TYPES, usersUtil } from 'services/utils/users-util';

class DepositDetails extends Component {
  constructor(props) {
    super(props);

    // Keep track of id to avoid showing an "old" deposit while loading the current one
    this.depositId = numeral(this.props.match.params.id).value();
  }

  componentDidMount() {
    if (
      this.props.currentDeposit === null ||
      this.props.currentDeposit.id !== this.depositId
    ) {
      this.props
        .dispatch(loadDepositRequest(this.depositId))
        .then((response) => {
          if (response.ok) {
            if (
              this.props.usersByClientId[response.deposit.client.id] ===
              undefined
            ) {
              this.props.dispatch(loadUsersRequest(response.deposit.client.id));
            }
          }
        });
    }
  }

  mapTasks = (tasks) => {
    return tasks
      .filter((task) => task.status !== TaskStatusKeys.CLS)
      .map((task) => {
        return [
          <Number value={task.number} format="00" />,
          <span className="h4">{task.name || 'N/A'}</span>,
          '',
        ];
      });
  };

  handleMarkAsPaid = () => {
    this.props.dispatch(markAsPaidRequest(this.depositId));
  };

  handlePrint = () => {
    this.props.dispatch(printDeposit(this.depositId));
  };

  handlePdfViewerClose = () => {
    this.props.dispatch(closePdfViewer());
  };

  handleEmail = () => {
    this.props.dispatch(setShowEmailModal(true));
  };

  getEmailMessage = () => {
    let location = '';
    if (this.props.currentDeposit.building !== null) {
      const building = this.props.currentDeposit.building;
      location = ` at ${building.location.street_address_1}, ${building.location.city} ${building.location.state}`;
    }

    return `Please find the attached deposit invoice for WO #${this.props.currentDeposit.work_order.number}${location}. \
If you have any questions, please reach out via chat or email.

Thank you`;
  };

  getEmailSubject = () => {
    let name = '';
    if (WorkOrder.getName(this.props.currentDeposit.work_order) !== null) {
      name = ` - ${WorkOrder.getName(this.props.currentDeposit.work_order)}`;
    }
    return `Spackle Deposit Invoice for WO #${this.props.currentDeposit.work_order.number}-D${name}`;
  };

  getEmailTitle = () => {
    return `Deposit Invoice for WO #${this.props.currentDeposit.work_order.number}-D`;
  };

  handleEmailModalClose = () => {
    this.props.dispatch(setShowEmailModal(false));
  };

  handleEmailSend = (emails, subject, message) => {
    this.props.dispatch(
      sendEmail(this.props.currentDeposit.id, emails, subject, message)
    );
  };

  render() {
    if (
      this.props.currentDeposit !== null &&
      this.props.currentDeposit.id === this.depositId
    ) {
      const isPaid = this.props.currentDeposit.status === 'paid';
      const isSpackleAdmin =
        usersUtil.hasRole(this.props.currentUser, ROLES.SPACKLE_ADMIN) ||
        usersUtil.hasType(this.props.currentUser, TYPES.GENERAL_CONTRACTOR);

      let clientUsers = [];
      if (
        this.props.usersByClientId[this.props.currentDeposit.client.id] !==
        undefined
      ) {
        clientUsers = this.props.usersByClientId[
          this.props.currentDeposit.client.id
        ];
      }

      return (
        <div className="deposit-details">
          <RouteTitle
            titles={[
              'DEPOSITS',
              `DEPOSIT INVOICE WO #${this.props.currentDeposit.work_order.number}-D`,
            ]}
          />

          <div className="full-title">
            <span>
              Deposit Invoice (WO {this.props.currentDeposit.work_order.number}
              -D)
            </span>

            <div className="print-email-buttons">
              <Button
                className="button-gray"
                icon={mailWhiteSvg}
                label="Email"
                onClick={this.handleEmail}
              />
              <Button
                className="button-gray"
                icon={printWhiteSvg}
                label="Print"
                onClick={this.handlePrint}
              />
            </div>
          </div>

          <div className="content-container">
            <div className="invoice-data-container">
              <div className="invoice-data">
                <div className="table-container">
                  <div className="form-table">
                    <div>
                      <div className="invoice-data-label">To</div>
                      <div>{this.props.currentDeposit.client.company_name}</div>
                    </div>
                    <div>
                      <div className="invoice-data-label">Created Date</div>
                      <div className="invoice-data-bold">
                        <Date
                          value={this.props.currentDeposit.created_at}
                          format="M/D/YYYY"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="invoice-data-label">Deposit Amount</div>
                      <div className="invoice-data-bold orange">
                        <Number
                          value={this.props.currentDeposit.cost}
                          format="$ 0,0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="inv-buttons-container">
                {isSpackleAdmin && !isPaid && (
                  <Button
                    label="Mark as Paid"
                    className="button-orange"
                    onClick={this.handleMarkAsPaid}
                  />
                )}
                {isPaid && (
                  <div className="not-paid-indicator">
                    <img src={checkSquarePng} alt="yes" />
                    <div className="button button-white-orange">
                      <span>Paid</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="table-container">
              <SimpleTable
                className={'tasks-table'}
                headers={['TASK #', 'TASK NAME', '']}
                rows={this.mapTasks(this.props.currentDeposit.work_order.tasks)}
                emptyMessage="No Tasks"
                showBottomLine={true}
              />
              <SimpleTable
                className={'total-table'}
                rows={[
                  [
                    '',
                    <span className="total">DEPOSIT INVOICE TOTAL</span>,
                    <Number
                      className="h3"
                      value={this.props.currentDeposit.cost}
                      format="$0,0"
                    />,
                  ],
                ]}
              />
            </div>

            <hr />

            <div className="payment-container">
              <div className="payment-info">
                <div>Send Payment to:</div>
                <div>Spackle, Inc.</div>
                <div>4130 Cahuenga Blvd, Suite 206</div>
                <div>Los Angeles, CA 91602</div>
              </div>

              <div className="payment-info">
                <span>
                  Please note, our banking has changed. To pay via ACH please contact us at{' '}
                  <a href={'mailto:accounts@spackle.co'} className="mailto">
                    accounts@spackle.co
                  </a>.
                  <br />
                  To pay by credit card or for wiring instructions, please contact us at{' '}
                  <a href={'mailto:admin@spackle.co'} className="mailto">
                    admin@spackle.co
                  </a>.
                </span>
              </div>
            </div>
          </div>

          <PdfViewer
            content={{
              source: this.props.pdf,
              type: 'blob',
            }}
            show={this.props.showPdfViewer}
            onClose={this.handlePdfViewerClose}
          />

          <EmailModal
            defaultEmails={clientUsers
              .filter((user) => user.account_creator)
              .map((user) => user.email)}
            defaultMessage={this.getEmailMessage()}
            defaultSubject={this.getEmailSubject()}
            emails={clientUsers.map((user) => user.email)}
            title={this.getEmailTitle()}
            show={this.props.showEmailModal}
            onClose={this.handleEmailModalClose}
            onSend={this.handleEmailSend}
          />
        </div>
      );
    } else {
      return null;
    }
  }
}

export default connect((state) => {
  return {
    currentUser: getCurrentUser(state),
    currentDeposit: getCurrentDeposit(state),
    pdf: getPdf(state),
    showPdfViewer: getShowPdfViewer(state),
    usersByClientId: getUsersByClientId(state),
    showEmailModal: getShowEmailModal(state),
  };
})(DepositDetails);



// WEBPACK FOOTER //
// ./src/components/admin/invoices/DepositDetails.js